/**
 * @description Multi-step tenant creation form for admin portal
 *
 * Key features:
 * - Step 1: Template selection (card picker with feature preview)
 * - Step 2: Details (org name, slug with preview, contact, SO admin user)
 * - Step 3: Review & submit (summary of all selections)
 * - Idempotency key prevents duplicate tenant creation from retries
 * - Client-side email validation before API call
 *
 * @dependencies
 * - adminApi: getTemplates(), createTenant()
 * - react-router-dom: useNavigate
 * - lucide-react: icons
 *
 * @notes
 * - Template data shape from GET /api/admin/templates:
 *   { id, name, description, featureConfig: { feature_name: boolean }, isActive }
 * - POST /api/admin/tenants expects:
 *   { organizationName, slug, templateId, adminEmail, adminFirstName, adminLastName,
 *     contactEmail, contactPhone }
 * - Slug validation (format, reserved words, uniqueness) is done server-side
 * - Slug max 20 chars (Wristband tenant name constraint)
 * - Wristband M2M creates tenant + SO admin user + sends invite email
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, AlertCircle, Building2, Loader2,
  Briefcase, Layers
} from 'lucide-react';
import { getTemplates, createTenant } from '../../services/api/adminApi';
import { getSODomainUrl } from '../../config/domains';
import { US_IANA_TIMEZONES } from '../../constants/usIanaTimezones';

// Feature name display mapping (snake_case → readable)
const FEATURE_LABELS = {
  payment_gateways: 'Payment Gateways',
  invoicing: 'Invoicing',
  advanced_reporting: 'Advanced Reporting',
  payroll: 'Payroll',
  pos_systems: 'POS Systems',
};

// Template icon mapping
const TEMPLATE_ICONS = {
  'Accounting Firm': Briefcase,
  'Full Service': Layers,
};

// Simple email validation
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function TenantCreate() {
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [inviteNotice, setInviteNotice] = useState(null);
  const [createdHref, setCreatedHref] = useState(null);

  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState({
    organizationName: '',
    slug: '',
    contactEmail: '',
    contactPhone: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    timezone: '',
    sendInvite: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Load templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const result = await getTemplates();
        if (result.success) {
          setTemplates(result.data);
        }
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Auto-generate slug from organization name
  const updateForm = useCallback((field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-slug from org name (only if slug hasn't been manually edited)
      if (field === 'organizationName' && prev.slug === autoSlug(prev.organizationName)) {
        updated.slug = autoSlug(value);
      }

      return updated;
    });

    // Clear field error on change
    setFieldErrors(prev => {
      if (prev[field]) {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      }
      return prev;
    });
  }, []);

  // Validate step 2 fields
  const validateStep2 = () => {
    const errors = {};

    if (!form.organizationName.trim() || form.organizationName.trim().length < 2) {
      errors.organizationName = 'Organization name is required (min 2 characters)';
    }
    if (!form.slug.trim()) {
      errors.slug = 'Slug is required';
    } else if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(form.slug) && form.slug.length > 1) {
      errors.slug = 'Slug must be lowercase letters, numbers, and hyphens (no leading/trailing hyphens)';
    } else if (form.slug.length < 3 || form.slug.length > 20) {
      errors.slug = 'Slug must be 3-20 characters';
    }
    if (!form.adminEmail.trim()) {
      errors.adminEmail = 'Admin email is required';
    } else if (!isValidEmail(form.adminEmail.trim())) {
      errors.adminEmail = 'Please enter a valid email address';
    }
    if (form.contactEmail.trim() && !isValidEmail(form.contactEmail.trim())) {
      errors.contactEmail = 'Please enter a valid email address';
    }
    if (!form.timezone) {
      errors.timezone = 'Timezone is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && !selectedTemplate) return;
    if (step === 2 && !validateStep2()) return;
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setSubmitError(null);
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setInviteNotice(null);

    // Generate idempotency key to prevent duplicate creation
    const idempotencyKey = crypto.randomUUID();

    try {
      const result = await createTenant({
        organizationName: form.organizationName.trim(),
        slug: form.slug.toLowerCase().trim(),
        templateId: selectedTemplate.id,
        adminEmail: form.adminEmail.trim().toLowerCase(),
        adminFirstName: form.adminFirstName.trim() || undefined,
        adminLastName: form.adminLastName.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        timezone: form.timezone,
        sendInvite: form.sendInvite === true,
      }, idempotencyKey);

      if (result.success) {
        if (form.sendInvite === true && result.data?.inviteSent !== true) {
          setInviteNotice(
            result.message
            || 'Sales organization created but invitation could not be sent. You can resend later.'
          );
          setCreatedHref(`/tenants/${result.data.id}`);
          return;
        }
        navigate(`/tenants/${result.data.id}`, { replace: true });
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      if (errorCode === 'INVALID_SLUG' || errorCode === 'SLUG_TOO_LONG') {
        setSubmitError(errorMessage || 'Invalid slug. Please choose a different one.');
        setStep(2);
      } else if (errorCode === 'VALIDATION_ERROR') {
        setSubmitError(errorMessage || 'Please check your input and try again.');
        setStep(2);
      } else {
        setSubmitError(errorMessage || 'Failed to create tenant. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load templates</p>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/tenants')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tenants
        </button>
        <h1 className="text-xl font-semibold text-gray-900">Create Tenant</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Set up a new sales organization with template-based configuration
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium transition-colors ${
              s < step
                ? 'bg-indigo-600 text-white'
                : s === step
                  ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-600'
                  : 'bg-gray-100 text-gray-400'
            }`}>
              {s < step ? <Check className="w-3.5 h-3.5" /> : s}
            </div>
            <span className={`text-xs font-medium ${s === step ? 'text-gray-900' : 'text-gray-400'}`}>
              {s === 1 ? 'Template' : s === 2 ? 'Details' : 'Review'}
            </span>
            {s < 3 && <div className={`w-12 h-px ${s < step ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Template Selection */}
      {step === 1 && (
        <div>
          <h2 className="text-base font-medium text-gray-900 mb-1">Choose a template</h2>
          <p className="text-sm text-gray-500 mb-4">Templates pre-configure feature sets for the new tenant</p>

          <div className="grid gap-3">
            {templates.map((template) => {
              const Icon = TEMPLATE_ICONS[template.name] || Building2;
              const isSelected = selectedTemplate?.id === template.id;
              const enabledFeatures = Object.entries(template.featureConfig || {})
                .filter(([, v]) => v)
                .map(([k]) => k);
              const disabledFeatures = Object.entries(template.featureConfig || {})
                .filter(([, v]) => !v)
                .map(([k]) => k);

              return (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-indigo-600 bg-indigo-50/50 ring-1 ring-indigo-600'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{template.name}</p>
                        {isSelected && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>

                      {/* Feature tags */}
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {enabledFeatures.map((feat) => (
                          <span key={feat} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 font-medium">
                            {FEATURE_LABELS[feat] || feat}
                          </span>
                        ))}
                        {disabledFeatures.map((feat) => (
                          <span key={feat} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-400 line-through">
                            {FEATURE_LABELS[feat] || feat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Details */}
      {step === 2 && (
        <div>
          <h2 className="text-base font-medium text-gray-900 mb-1">Tenant details</h2>
          <p className="text-sm text-gray-500 mb-4">Enter organization information and SO admin credentials</p>

          <div className="space-y-5">
            {/* Organization section */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.organizationName}
                  onChange={(e) => updateForm('organizationName', e.target.value)}
                  placeholder="Acme Accounting"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                    fieldErrors.organizationName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {fieldErrors.organizationName && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.organizationName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => updateForm('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 20))}
                  maxLength={20}
                  placeholder="acme-accounting"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                    fieldErrors.slug ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                />
                <p className={`text-xs mt-1 ${fieldErrors.slug ? 'text-red-600' : 'text-gray-400'}`}>
                  {fieldErrors.slug || (
                    <>Domain preview: <span className="font-medium text-gray-600">{getSODomainUrl(form.slug || 'slug')}</span></>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => updateForm('contactEmail', e.target.value)}
                    placeholder="billing@acme.com"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                      fieldErrors.contactEmail ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  {fieldErrors.contactEmail && (
                    <p className="text-xs text-red-600 mt-1">{fieldErrors.contactEmail}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={form.contactPhone}
                    onChange={(e) => updateForm('contactPhone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.timezone}
                  onChange={(e) => updateForm('timezone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                    fieldErrors.timezone ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                >
                  <option value="">Select timezone</option>
                  {US_IANA_TIMEZONES.map((zone) => (
                    <option key={zone.value} value={zone.value}>
                      {zone.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.timezone && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.timezone}</p>
                )}
              </div>
            </div>

            {/* Admin User section */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Admin User</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.adminEmail}
                  onChange={(e) => updateForm('adminEmail', e.target.value)}
                  placeholder="admin@acme.com"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                    fieldErrors.adminEmail ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {fieldErrors.adminEmail && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.adminEmail}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={form.adminFirstName}
                    onChange={(e) => updateForm('adminFirstName', e.target.value)}
                    placeholder="Jane"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={form.adminLastName}
                    onChange={(e) => updateForm('adminLastName', e.target.value)}
                    placeholder="Doe"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sendInvite}
                  onChange={(e) => updateForm('sendInvite', e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  <span className="text-sm font-medium text-gray-700">Send invitation</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Email a branded invite so they can set a password. Leave unchecked to create only.
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div>
          <h2 className="text-base font-medium text-gray-900 mb-1">Review & create</h2>
          <p className="text-sm text-gray-500 mb-4">Confirm the details before creating this tenant</p>

          {submitError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
          {inviteNotice && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span>{inviteNotice}</span>
                {createdHref && (
                  <button
                    type="button"
                    onClick={() => navigate(createdHref, { replace: true })}
                    className="mt-2 block text-sm font-medium text-amber-900 underline"
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
            {/* Template */}
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Template</p>
              <p className="text-sm font-medium text-gray-900">{selectedTemplate?.name}</p>
              <p className="text-xs text-gray-500">{selectedTemplate?.description}</p>
            </div>

            {/* Organization */}
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Organization</p>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Name</p>
                  <p className="text-gray-900 font-medium">{form.organizationName}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Slug</p>
                  <p className="text-gray-900 font-medium">{form.slug}</p>
                  <p className="text-xs text-gray-400">{getSODomainUrl(form.slug)}</p>
                </div>
                {form.contactEmail && (
                  <div>
                    <p className="text-gray-500 text-xs">Contact Email</p>
                    <p className="text-gray-900">{form.contactEmail}</p>
                  </div>
                )}
                {form.contactPhone && (
                  <div>
                    <p className="text-gray-500 text-xs">Contact Phone</p>
                    <p className="text-gray-900">{form.contactPhone}</p>
                  </div>
                )}
                {form.timezone && (
                  <div>
                    <p className="text-gray-500 text-xs">Timezone</p>
                    <p className="text-gray-900">{form.timezone}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Admin User */}
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Admin User</p>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Email</p>
                  <p className="text-gray-900 font-medium">{form.adminEmail}</p>
                </div>
                {(form.adminFirstName || form.adminLastName) && (
                  <div>
                    <p className="text-gray-500 text-xs">Name</p>
                    <p className="text-gray-900">{form.adminFirstName} {form.adminLastName}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 text-xs">Send invitation</p>
                  <p className="text-gray-900">{form.sendInvite ? 'Yes' : 'No — create only'}</p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Feature Set</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(selectedTemplate?.featureConfig || {}).map(([feat, enabled]) => (
                  <span
                    key={feat}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      enabled
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-400 line-through'
                    }`}
                  >
                    {FEATURE_LABELS[feat] || feat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
        {step > 1 ? (
          <button
            onClick={handleBack}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        ) : (
          <div />
        )}

        {step < 3 ? (
          <button
            onClick={handleNext}
            disabled={step === 1 && !selectedTemplate}
            className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Create Tenant
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Auto-generate slug from organization name
 * "Acme Accounting" → "acme-accounting"
 */
function autoSlug(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 20)
    .replace(/-$/, '');
}

export default TenantCreate;
